import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Paper, Tab, Tabs } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ClientNoData from '../components/Client/ClientNoData';
import { CLIENT_PAGE } from '../utils/commonConstants';
import {
  getAllPaginatedClientsApi,
  getAllFavourites,
  addClientToFavourite,
  removeClientFromFavourite,
} from "../api's/ClientApi";
import { getAllDomainsApi } from "../api's/DomainApi";
import ClientTable from '../components/dashboard-component/ClientTable';
import PageHeader from '../reusable-component/PageHeader';
import SearchAndFilter from '../reusable-component/SearchAndFilter';
import LoadingIcon from '../reusable-component/LoadingIcon';

const ClientPage = () => {
  const rowsPerPage = 5;
  const { enqueueSnackbar } = useSnackbar();
  const isTabInitialized = useRef(false);
  const [tabValue, setTabValue] = useState(() => {
    const storedTab = sessionStorage.getItem('client_tab_value');
    return storedTab !== null ? parseInt(storedTab, 10) : 0;
  });
  const [allDomains, setAllDomains] = useState([]);
  const [search, setSearch] = useState(sessionStorage.getItem('filtered_client_search') || '');
  const [status, setStatus] = useState(sessionStorage.getItem('filtered_client_status') || '');
  const [region, setRegion] = useState(sessionStorage.getItem('filtered_domain') || '');
  const [page, setPage] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [favouriteClients, setFavouriteClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef(null);

  const userPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');
  const hasViewFavoritesPermission = userPermissions.includes('VIEW_FAVORITES');
  const hasAddToFavoritesPermission = userPermissions.includes('ADD_TO_FAVORITES');
  const hasCreatePermission = userPermissions.includes('CREATE_CLIENT_ENGAGEMENT_WORKFLOW');

  const fetchData = useCallback(
    async (pageNum = 0, currentSearch = search, currentStatus = status, currentRegion = region) => {
      setLoading(true);
      try {
        const payload = {
          page: pageNum,
          size: rowsPerPage,
          clientName: currentSearch || null,
          domainName: currentRegion || null,
          ...(tabValue === 0 && { status: currentStatus.toLowerCase() || null }),
        };

        const response = tabValue === 0 ? await getAllPaginatedClientsApi(payload) : await getAllFavourites(payload);

        if (response.status === 200) {
          const { clientData = [], totalData = 0 } = response.data;
          tabValue === 0 ? setTableData(clientData) : setFavouriteClients(clientData);
          setTotalCount(totalData);
        }
      } catch (err) {
        enqueueSnackbar('Failed to load clients', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        tabValue === 0 ? setTableData([]) : setFavouriteClients([]);
      } finally {
        setLoading(false);
      }
    },
    [tabValue, search, status, region]
  );

  const debouncedFetch = useCallback(
    (pageNum = 0, currentSearch = search, currentStatus = status, currentRegion = region) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchData(pageNum, currentSearch, currentStatus, currentRegion);
      }, 500);
    },
    [fetchData]
  );

  const handleSearchInput = (e) => {
    const newSearch = e.target.value;
    sessionStorage.setItem('filtered_client_search', newSearch);
    setSearch(newSearch);
    setPage(0);
    debouncedFetch(0, newSearch, status, region);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    sessionStorage.setItem('filtered_client_status', newStatus);
    setStatus(newStatus);
    setPage(0);
    fetchData(0, search, newStatus, region);
  };

  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    sessionStorage.setItem('filtered_domain', newRegion);
    setRegion(newRegion);
    setPage(0);
    fetchData(0, search, status, newRegion);
  };

  const handleClearFilter = () => {
    const isAnyFilterApplied = search || status || region;
    if (!isAnyFilterApplied) return;
    sessionStorage.removeItem('filtered_client_search');
    sessionStorage.removeItem('filtered_client_status');
    sessionStorage.removeItem('filtered_domain');
    setSearch('');
    setStatus('');
    setRegion('');
    setPage(0);
    fetchData(0, '', '', '');
  };

  useEffect(() => {
    fetchData(0, search, status, region); // use stored filters
  }, []); // only once

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
    sessionStorage.setItem('client_tab_value', newValue);
    // handleClearFilter();
    setPage(0);
    //  fetchData(0, search, status, region);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    fetchData(newPage, search, status, region);
  };

  const toggleFavoriteClient = async (clientId) => {
    if (!hasAddToFavoritesPermission) {
      enqueueSnackbar('You do not have permission to add to favorites', { variant: 'error' });
      return;
    }

    try {
      const allClients = tabValue === 0 ? tableData : favouriteClients;
      const selectedClient = allClients.find((c) => c.client_id === clientId);
      const isFavorite = selectedClient?.is_fav || false;
      const apiCall = isFavorite ? removeClientFromFavourite : addClientToFavourite;

      const response = await apiCall(clientId);
      if (response.status === 200) {
        enqueueSnackbar(isFavorite ? 'Removed from favorites' : 'Added to favorites', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        fetchData(page, search, status, region);
      }
    } catch {
      enqueueSnackbar('Failed to update favorites', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await getAllDomainsApi();
        if (res.status === 200 && Array.isArray(res.data)) {
          setAllDomains(res.data.map((d) => d.domain_name).filter(Boolean));
        }
      } catch (err) {
        enqueueSnackbar('Failed to load domain list', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    };

    fetchDomains();
  }, []);

  useEffect(() => {
    if (isTabInitialized.current) {
      fetchData(0, search, status, region);
    }
  }, [tabValue]);

  useEffect(() => {
    const storedTab = sessionStorage.getItem('client_tab_value');
    if (storedTab !== null) {
      const parsedTab = parseInt(storedTab, 10);
      setTabValue(parsedTab);
    }
    isTabInitialized.current = true;
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchData(0, search, status, region);
  };

  const filterData = {
    search,
    status,
    region,
    handleSearch,
    handleOnChange: handleSearchInput,
    handleStatus: handleStatusChange,
    handleRegion: handleRegionChange,
    handleClearFilter,
    firstFilterText: CLIENT_PAGE.clientFilterByDomain,
    secondFilterText: CLIENT_PAGE.clientFilterByStatus,
    clearFilterText: CLIENT_PAGE.clientClearFilterButton,
    firstFilterValues: {
      filterData: allDomains,
    },
    secondFilterValues: {
      filterData: ['Approved', 'Rejected', 'Pending'],
    },
    isFirstFilterVisible: true,
    isSecondFilterVisible: true,
    isThirdFilterVisible: false,
  };

  return (
    <Box>
      <PageHeader
        title={CLIENT_PAGE.clientPageTitle}
        btnText={CLIENT_PAGE.addNewClientButton}
        route="/client/onboarding"
        disabled={!hasCreatePermission}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: '#47D7AC' } }}
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#00043C' } }}
        >
          <Tab label="All" sx={{ fontWeight: 'bold' }} />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Favourite
                {!hasViewFavoritesPermission && <LockOutlined sx={{ fontSize: 16 }} />}
              </Box>
            }
            sx={{ fontWeight: 'bold' }}
            disabled={!hasViewFavoritesPermission}
          />
        </Tabs>
      </Box>

      <SearchAndFilter data={filterData} />

      <Box mt={2}>
        {loading ? (
          <LoadingIcon />
        ) : (tabValue === 0 ? tableData : favouriteClients).length > 0 ? (
          <ClientTable
            tableData={tabValue === 0 ? tableData : favouriteClients}
            fetchTableData={fetchData}
            currentPage={page}
            setPage={setPage}
            search={search}
            totalCount={totalCount}
            favouriteClients={favouriteClients}
            toggleFavoriteClient={toggleFavoriteClient}
            handleChangePage={handleChangePage}
          />
        ) : (
          <Paper elevation={3} sx={{ height: 350 }}>
            <ClientNoData
              title={CLIENT_PAGE.clientPageNoDataToDisplay}
              text={tabValue === 0 ? CLIENT_PAGE.noClientToReview : 'You have no favourite clients.'}
              addBtnText={tabValue === 1 ? 'Browse Clients' : undefined}
              // addBtnRoute="/client/onboarding"
              // isRoute={tabValue === 0}
              isAddBtn={tabValue === 1}
              handleOnClick={() => setTabValue(0)}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ClientPage;
